import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useMediaQuery } from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';

import { keepSearch } from '../utils';

import { DocsPanelApi, DocsPanelContext } from './context';
import {
  clampPanelWidth,
  documentationUrl,
  DEFAULT_PANEL_WIDTH,
  DocsPanelState,
  isDocumentationPath,
  PANEL_MIN_SCREEN_WIDTH,
} from './utils';

/**
 * Owns the follow-along panel: which docs page sits beside the app, at what
 * section and width. Lives above the routes so it survives navigation. Its
 * state is in memory only, so a reload always brings back the plain app.
 */
export const DocsPanelProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Read the screen size right away (no SSR here), so the chip never flashes.
  const [panelFits] = useMediaQuery(
    `(min-width: ${PANEL_MIN_SCREEN_WIDTH}px)`,
    { ssr: false },
  );
  const [state, setState] = useState<DocsPanelState>({
    open: false,
    width: DEFAULT_PANEL_WIDTH,
  });

  // Keep the panel within half the window when the window gets smaller.
  useEffect(() => {
    const fit = () =>
      setState(current => {
        const width = clampPanelWidth(current.width, window.innerWidth);
        return width === current.width ? current : { ...current, width };
      });
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  // Remember the last app screen, the place "Follow along" returns to.
  useEffect(() => {
    if (isDocumentationPath(location.pathname)) return;
    const lastAppLocation = `${location.pathname}${location.search}`;
    setState(current =>
      current.lastAppLocation === lastAppLocation
        ? current
        : { ...current, lastAppLocation },
    );
  }, [location.pathname, location.search]);

  const followAlong = useCallback(
    (path: string, anchor?: string, to?: string) => {
      const target = to ?? state.lastAppLocation ?? '/';
      setState(current => ({ ...current, open: true, path, anchor }));
      navigate(keepSearch(target, location.search));
    },
    [location.search, navigate, state.lastAppLocation],
  );

  const api = useMemo<DocsPanelApi>(
    () => ({
      state,
      panelFits,
      followAlong,
      openDocument: (path, anchor) =>
        setState(current => ({ ...current, open: true, path, anchor })),
      setAnchor: anchor =>
        setState(current =>
          current.anchor === anchor ? current : { ...current, anchor },
        ),
      setWidth: width =>
        setState(current => ({
          ...current,
          width: clampPanelWidth(width, window.innerWidth),
        })),
      expand: () => {
        if (!state.path) return;
        setState(current => ({ ...current, open: false }));
        navigate(
          keepSearch(
            documentationUrl(state.path, state.anchor),
            location.search,
          ),
        );
      },
      // Closing ends following along: nothing comes back until it's chosen
      // again from a docs page.
      close: () =>
        setState(current => ({
          ...current,
          open: false,
          path: undefined,
          anchor: undefined,
        })),
    }),
    [followAlong, location.search, navigate, panelFits, state],
  );

  return (
    <DocsPanelContext.Provider value={api}>
      {children}
    </DocsPanelContext.Provider>
  );
};
